import React from 'react';
import UIStatCardProperty, { PropertyStatItem } from '@/components/shared/UIStatCardProperty';
import UISectionWrapper from '@/components/shared/UISectionWrapper';
import useDashboardStatistics from '@/hooks/useDashboardStatistics';
import Skeleton from '@/components/shared/Skeleton';
import './PropertiesStatsSection.scss';

interface PropertiesStatsSectionProps {
  /**
   * Обробники подій
   */
  onCardClick?: (type: string) => void;
  onMenuClick?: (type: string) => void;

  /**
   * Додатковий CSS клас
   */
  className?: string;
}

export type { PropertiesStatsSectionProps };

const baseClass = 'DashboardPage__PropertiesStatsSection';

const PropertiesStatsSection: React.FC<PropertiesStatsSectionProps> = ({
                                                                         onCardClick = (type) => console.log('Card clicked:', type),
                                                                         onMenuClick = (type) => console.log('Menu clicked:', type),
                                                                         className = '',
                                                                       }) => {
  const { data: dashboardData, isLoading, error } = useDashboardStatistics();

  const handleCardClick = (type: string) => {
    onCardClick(type);
  };

  const handleMenuClick = (type: string) => {
    onMenuClick(type);
  };

  // Функція для конвертації даних з API в формат для UI компонентів
  const convertToPropertyStats = (statistics: any): PropertyStatItem[] => {
    // Фільтруємо тільки ті статуси, які мають значення більше 0
    const items = [
      {
        label: 'Paid',
        count: statistics.paid.count,
        percentage: statistics.paid.percentage,
        color: '#76C102',
      },
      {
        label: 'Arrears',
        count: statistics.arrears.count,
        percentage: statistics.arrears.percentage,
        color: '#FB2F54',
      },
      {
        label: 'Not set',
        count: statistics.notSet.count,
        percentage: statistics.notSet.percentage,
        color: '#FF9C07',
      },
      {
        label: 'Pending',
        count: statistics.pending.count,
        percentage: statistics.pending.percentage,
        color: '#FFD700',
      },
    ].filter(item => item.count > 0); // Показуємо тільки категорії з даними

    return items.map(item => ({
      label: item.label,
      value: `${item.count} (${item.percentage}%)`, // Показуємо і кількість і відсоток
      color: item.color,
      percentage: item.percentage,
    }));
  };

  // Loading state
  if (isLoading) {
    return (
      <UISectionWrapper
        className={`${baseClass} ${className}`}
        spacing="default"
        align="stretch"
        shadow="none"
        background="transparent"
      >
        <div className={`${baseClass}__grid`}>
          {[...Array(3)].map((_, index) => (
            <div key={index} className={`${baseClass}__card`}>
              <Skeleton isLoading={true} style={{ width: '100%', height: '184px' }} />
            </div>
          ))}
        </div>
      </UISectionWrapper>
    );
  }

  // Error state
  if (error || !dashboardData?.success) {
    return (
      <UISectionWrapper
        className={`${baseClass} ${className}`}
        spacing="default"
        align="stretch"
        shadow="none"
        background="transparent"
      >
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Failed to load properties statistics</p>
          <p style={{ fontSize: '14px', color: '#666' }}>
            {error?.message || 'Unknown error occurred'}
          </p>
        </div>
      </UISectionWrapper>
    );
  }

  const buildings = dashboardData.data.buildings;

  return (
    <UISectionWrapper
      className={`${baseClass} ${className}`}
      spacing="default"
      align="stretch"
      shadow="none"
      background="transparent"
    >
      <div className={`${baseClass}__grid`}>
        {/* Residential buildings */}
        {buildings.residential && (
          <UIStatCardProperty
            title="Residential buildings"
            iconType="home"
            iconBackgroundColor="#5CB2EF"
            totalUnits={buildings.residential.totalUnits}
            stats={convertToPropertyStats(buildings.residential.statistics)}
            onClick={() => handleCardClick('residential')}
            onMenuClick={() => handleMenuClick('residential')}
            className={`${baseClass}__card`}
          />
        )}

        {/* Commercial buildings */}
        {buildings.commercial && (
          <UIStatCardProperty
            title="Commercial buildings"
            iconType="dollar"
            iconBackgroundColor="#5CB2EF"
            totalUnits={buildings.commercial.totalUnits}
            stats={convertToPropertyStats(buildings.commercial.statistics)}
            onClick={() => handleCardClick('commercial')}
            onMenuClick={() => handleMenuClick('commercial')}
            className={`${baseClass}__card`}
          />
        )}

        {/* Mixed buildings */}
        {buildings.mixed && (
          <UIStatCardProperty
            title="Mixed buildings"
            iconType="factory"
            iconBackgroundColor="#5CB2EF"
            totalUnits={buildings.mixed.totalUnits}
            stats={convertToPropertyStats(buildings.mixed.statistics)}
            onClick={() => handleCardClick('mixed')}
            onMenuClick={() => handleMenuClick('mixed')}
            className={`${baseClass}__card`}
          />
        )}

        {/* Якщо немає даних для жодного типу */}
        {!buildings.residential && !buildings.commercial && !buildings.mixed && (
          <div style={{ textAlign: 'center', padding: '40px', width: '100%' }}>
            <p>No building data available</p>
          </div>
        )}
      </div>
    </UISectionWrapper>
  );
};

export default PropertiesStatsSection;